import {
  useCapTableTransactions,
  useCompanyData,
  useGetWorkRequestsByCompany,
  useJurisdictions,
} from '@commenda/api';
import { IncorporationRequestDataType } from '@commenda/types';
import { useGetAllDirectorsInCompany } from '@commenda/api/director';
import { EditIcon01, ProceedIcon } from '@commenda/icons';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
} from '@commenda/kakashi';
import { TSetNotification } from '@commenda/types/src/random';
import { WorkRequestResponseType } from '@commenda/types/src/work-request/types';
import {
  fetcher,
  getCorporationNameFromWorkRequest,
  NonTimezoneSensitiveDate,
} from '@commenda/utils';
import {
  AddressType,
  CorporationType,
  IncorporationRequestStatus,
} from '@prisma/client';
import { Country, State } from 'country-state-city';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import { KeyedMutator } from 'swr';
import { USResponsibleParty } from '../../../service-request/US_INCORP/common/summary/responsible-party';
import {
  IncorporationFormHeading,
  SubmitWorkRequestModalKS,
  SummaryFieldKS,
} from '../common';
import CorporateShareholdersSummary from '../common/CorporateShareholdersSummary';
import { extractCorporateShareholders } from '../common/corporateStakeholder/utils';
import USFounderField from './USFounderField';

const SummarySection = ({
  setActiveStep,
  onProceed,
  data,
  mutate,
  hideActionButtons = false,
  isReviewSummary = false,
  setNotification,
  token,
  corporationId,
  companyId,
}: {
  setActiveStep: (stepIdx: number) => void;
  onProceed: (idx: number) => void;
  data: WorkRequestResponseType & { requestData: IncorporationRequestDataType };
  mutate: KeyedMutator<unknown>;
  hideActionButtons?: boolean;
  isReviewSummary?: boolean;
  token: string;
  setNotification: TSetNotification;
  corporationId: number;
  companyId: number;
}) => {
  const { mutate: mutateCompanyData } = useCompanyData(companyId);
  const { labelOf } = useJurisdictions();
  const { mutate: mutateWorkRequests } = useGetWorkRequestsByCompany(companyId);
  const { data: capTableTransactions } = useCapTableTransactions(corporationId);
  const { data: directorsData } = useGetAllDirectorsInCompany(companyId);

  const responsibleParty = directorsData?.directors?.find((director) => {
    return data?.corporation?.corporationHasDirector?.some(
      (corpHasDir) =>
        corpHasDir.directorId === director.id &&
        corpHasDir.additionalDirectorInformation?.isResponsibleParty
    );
  });

  // Get individual shareholders from cap table transactions
  const individualShareholders = useMemo(() => {
    if (!capTableTransactions || !directorsData?.directors) return [];

    // Get director IDs from cap table transactions
    const directorIdsFromCapTable = capTableTransactions
      .map((tx) => {
        const director = tx.stakeholding?.stakeholder?.director;
        if (!director) return null; // skip corporate shareholders
        return director.id;
      })
      .filter(Boolean);

    // Get full director data from directorsData
    const shareholders = directorsData.directors.filter((director) =>
      directorIdsFromCapTable.includes(director.id)
    );

    return shareholders;
  }, [capTableTransactions, directorsData]);

  const shareholderDirectorIds = useMemo(
    () => new Set(individualShareholders.map((shareholder) => shareholder.id)),
    [individualShareholders]
  );

  const router = useRouter();

  const isCorporateSubsidiary = false;

  const { id } = router.query as { id: string };

  const [isSubmitting, setSubmitting] = useState(false);

  const physicalAddress = data?.addresses?.find(
    (address) => address.type === AddressType.OperatingAddress
  )?.value;

  const mailingAddress = data?.addresses?.find(
    (address) => address.type === AddressType.MailingAddress
  )?.value;

  const onSubmit = async () => {
    setSubmitting(true);

    const response = await fetcher(
      `workRequest/${id}`,
      {
        method: 'POST',
        body: {
          status: IncorporationRequestStatus.Submitted,
        },
      },
      token
    );

    if (!response.ok) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message:
          'Something went wrong, please try again or contact Team Commenda if you need help.',
      });
      setSubmitting(false);
      return;
    }

    setSubmitting(false);
    await mutateWorkRequests();
    await mutateCompanyData();
    router.back();
  };

  return (
    <div className="flex flex-col gap-8 max-w-[700px]">
      <IncorporationFormHeading
        text="Summary"
        additionalText="Please verify the following information before submitting. Once submitted, you may not be able to make changes."
      />
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h4
            className="body-16-bold text-text-primary
          "
          >
            Company Info
          </h4>
          {hideActionButtons ? null : (
            <EditIcon01
              className="cursor-pointer"
              onClick={() => setActiveStep(0)}
            />
          )}
        </div>
        <div className="flex flex-col gap-3 bg-bg-disabled-subtle rounded-sm">
          <div className="flex flex-col gap-2 p-6">
            <SummaryFieldKS
              heading="Name"
              value={getCorporationNameFromWorkRequest(data?.requestData)}
            />
            {(data?.requestData?.companyLegalName2 ||
              data?.corporation?.corporationType === CorporationType.LLC) && (
              <SummaryFieldKS
                heading="Fallback Name"
                value={
                  getCorporationNameFromWorkRequest(
                    data?.requestData,
                    'companyLegalName2'
                  ) || 'Not provided'
                }
              />
            )}
            <SummaryFieldKS
              heading="Corporation Type"
              value={
                data?.corporation?.corporationType === CorporationType.LLC
                  ? 'LLC'
                  : 'C Corp'
              }
            />
            <SummaryFieldKS
              heading="Jurisdiction"
              value={
                !data?.requestData?.incorporationJurisdiction
                  ? 'Not sure'
                  : labelOf(data.requestData.incorporationJurisdiction)
              }
            />
            <SummaryFieldKS
              heading="Description"
              value={data?.requestData?.businessDescription}
            />
            <SummaryFieldKS
              heading="Company Website"
              value={data?.requestData?.socialLink}
            />
            <SummaryFieldKS
              heading="Fiscal Year End"
              value={
                data?.requestData?.fiscalMonth
                  ? (() => {
                      const startMonth = Number(data.requestData.fiscalMonth);
                      const endMonth = startMonth === 1 ? 12 : startMonth - 1;
                      return NonTimezoneSensitiveDate.getMonthNames()[
                        endMonth - 1
                      ];
                    })()
                  : null
              }
            />
            <SummaryFieldKS
              heading="Opt in to Commenda's registered address service."
              value={
                data?.requestData?.optForRegisterredAddressService
                  ? 'Yes'
                  : 'No'
              }
            />
            {physicalAddress ? (
              <SummaryFieldKS
                heading="Principal place of business"
                value={[
                  `${physicalAddress?.addressLine1}, ${physicalAddress?.addressLine2 || ''}`,
                  `${physicalAddress?.city} - ${physicalAddress?.postalCode}`,
                  `${
                    State.getStateByCodeAndCountry(
                      physicalAddress?.state,
                      physicalAddress?.country
                    )?.name
                  }, ${
                    Country.getCountryByCode(physicalAddress?.country)?.name
                  }`,
                ]}
              />
            ) : null}
            {data?.requestData?.corporationType === CorporationType.LLC ? (
              <SummaryFieldKS
                heading="LLC Management Type"
                value={data?.requestData?.isMemberManagedOrManagerManaged}
              />
            ) : null}
            {mailingAddress ? (
              <SummaryFieldKS
                heading="Mailing Address"
                value={[
                  `${mailingAddress?.addressLine1}, ${mailingAddress?.addressLine2 || ''}`,
                  `${mailingAddress?.city} - ${mailingAddress?.postalCode}`,
                  `${
                    State.getStateByCodeAndCountry(
                      mailingAddress?.state,
                      mailingAddress?.country
                    )?.name
                  }, ${
                    Country.getCountryByCode(mailingAddress?.country)?.name
                  }`,
                ]}
              />
            ) : null}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h4
            className="body-16-bold text-text-primary
          "
          >
            Directors & Officers
          </h4>
          {hideActionButtons ? null : (
            <EditIcon01
              className="cursor-pointer"
              onClick={() => setActiveStep(1)}
            />
          )}
        </div>
        <div className="flex flex-col gap-3 rounded-sm">
          <Accordion
            className="AccordionRoot flex flex-col gap-3"
            type="single"
            collapsible
          >
            {data?.directors
              ?.filter((eachDirector) =>
                eachDirector.corporationHasDirector.find(
                  (chd) =>
                    chd.corporationId === corporationId &&
                    chd.directorId === eachDirector.id &&
                    (chd.additionalDirectorInformation?.isDirector ||
                      shareholderDirectorIds.has(eachDirector.id) ||
                      chd.additionalDirectorInformation?.boardPosition?.length)
                )
              )
              .map((dir) => (
                <AccordionItem
                  key={dir.id}
                  className="AccordionItem border border-border-primary rounded-md"
                  value={dir.id?.toString()}
                >
                  <AccordionTrigger className="w-full bg-bg-disabled-subtle">
                    <div className="py-3 px-6 flex justify-between items-center w-full text-text-placeholder">
                      <span className="text-base ">
                        {dir?.directorFirstName} {dir?.directorLastName}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="p-4 pt-0">
                    <USFounderField
                      key={dir.id}
                      director={dir}
                      data={data}
                      shareholderDirectorIds={shareholderDirectorIds}
                    />
                  </AccordionContent>
                </AccordionItem>
              ))}
          </Accordion>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        {individualShareholders.length > 0 && (
          <>
            <div className="flex items-center justify-between">
              <h4 className="body-16-bold text-text-primary">
                Individual Shareholders
              </h4>
              {hideActionButtons ||
              individualShareholders?.length === 0 ? null : (
                <EditIcon01
                  className="cursor-pointer"
                  onClick={() => setActiveStep(3)}
                />
              )}
            </div>
            <div className="flex flex-col gap-3 rounded-sm">
              <Accordion
                className="AccordionRoot flex flex-col gap-3"
                type="single"
                collapsible
              >
                {individualShareholders.map((dir) => (
                  <AccordionItem
                    className="AccordionItem border border-border-primary rounded-md"
                    value={dir.id?.toString()}
                    key={dir.id}
                  >
                    <AccordionTrigger className="w-full bg-bg-disabled-subtle">
                      <div className="py-3 px-6 flex justify-between items-center w-full text-text-placeholder">
                        <span className="text-base ">
                          {dir?.directorFirstName} {dir?.directorLastName}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className=" p-4 pt-0">
                      <USFounderField
                        key={dir.id}
                        director={dir}
                        data={data}
                        shareholderDirectorIds={shareholderDirectorIds}
                      />
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </>
        )}
      </div>

      <div className="flex flex-col gap-3">
        {extractCorporateShareholders(capTableTransactions).length > 0 && (
          <>
            <div className="flex items-center justify-between">
              <h4 className="body-16-bold text-text-primary">
                Corporate Shareholders
              </h4>
              {hideActionButtons ? null : (
                <EditIcon01
                  className="cursor-pointer"
                  onClick={() => setActiveStep(3)}
                />
              )}
            </div>
            <CorporateShareholdersSummary
              corporateShareholders={extractCorporateShareholders(
                capTableTransactions
              )}
              directorsData={directorsData?.directors}
            />
          </>
        )}

        {/* Responsible Party Section */}
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <h4 className="body-16-bold text-text-primary">
              Responsible Party
            </h4>
            {hideActionButtons ? null : (
              <EditIcon01
                className="cursor-pointer"
                onClick={() => setActiveStep(3)}
              />
            )}
          </div>
          <div className="flex flex-col gap-3 rounded-sm">
            <Accordion
              className="AccordionRoot flex flex-col gap-3"
              type="single"
              collapsible
            >
              <AccordionItem
                className="AccordionItem border border-border-primary rounded-md"
                value="responsible-party"
              >
                <AccordionTrigger className="w-full bg-bg-disabled-subtle">
                  <div className="py-3 px-6 flex justify-between items-center w-full text-text-placeholder">
                    <span className="text-base">
                      {responsibleParty?.directorFirstName || 'Not assigned'}{' '}
                      {responsibleParty?.directorLastName || ''}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="p-4 pt-0">
                  <USResponsibleParty data={data} />
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </div>

      {hideActionButtons ? (
        <Button className="w-fit" onClick={() => router.push('/')}>
          <ProceedIcon className="rotate-180 mr-2 h-4 w-4" />
          <span>Back to Home</span>
        </Button>
      ) : (
        <div className="flex gap-4">
          <Button
            variant="secondary"
            onClick={() => setActiveStep(isCorporateSubsidiary ? 2 : 3)}
          >
            <ProceedIcon className="rotate-180 mr-2 h-4 w-4" />
            <span>Back</span>
          </Button>
          <SubmitWorkRequestModalKS
            isSubmitting={isSubmitting}
            description="This action is irreversible. Once submitted, the details will be
            locked for filing. Please review before submitting."
            onSubmit={onSubmit}
          />
        </div>
      )}
    </div>
  );
};

export default SummarySection;
